import React from "react"
import { motion } from "framer-motion"
import { LocalInventoryLinks } from "../../../molecules/LocalInventoryLinks"
import {
  formatSeriesSlug,
  generateInventoryLink,
  parseDisclaimerBlocks,
  toUsd,
} from "../../../../helpers"
import tw from "twin.macro"
import {
  setDisclaimers,
  toggleDisclaimersModal,
} from "../../../../contexts/Disclaimers/actions"
import { useContext, useEffect, useState } from "react"
import { LanguageContext } from "../../../../contexts/Language"
import { DisclaimersContext } from "../../../../contexts/Disclaimers"
import { Disclaimer } from "../../../../contexts/Disclaimers/disclaimers.d"
import { FeaturedOfferCardProps } from "../../Homepage/HeroCarousel/HeroCarousel.d"
import { useTealiumContext } from "../../../../contexts/Tealium"
import useTealiumEvent from "../../../../hooks/Tealium/useTealiumEvent"
import { Link } from "../../../atoms/Link"
import useVehicleData from "../../../../hooks/Tealium/useVehicleData"

const LeaseFeatureOfferCardVariant: React.FC<FeaturedOfferCardProps> = ({
  featuredOfferType,
  featuredOffer,
  sortedTermRate,
  dealers,
  city,
  inventoryCount,
  inventoryLink,
  offersLength,
  ctaTextPrimary,
  ctaTextSecondary,
  year,
  slideIndex,
  extraTealiumValues,
}) => {
  const { _, language } = useContext(LanguageContext)
  let offerDetails: {
    name: any
    termType: any
    term: any
    valueType: any
    value: any
    dueAtSigning: any
    slug: string
    _id: any
  } | null = null
  const [{ disclaimers }, dispatch] = useContext(DisclaimersContext)
  const [slideCount, setSlideCount] = useState(0)

  // Tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useTealiumContext()
  const [vehicleData] = useVehicleData()

  //create disclaimer for global context from offers meta data
  useEffect(() => {
    // check if disclaimer already exists
    if (!disclaimers.some(d => d.code === featuredOffer?._id)) {
      const newDisclaimer: Disclaimer = {
        code: featuredOffer?._id,
        disclaimer:
          language === "es"
            ? featuredOffer?.disclaimerES
            : featuredOffer?.disclaimer,
        disclaimer_en: featuredOffer?.disclaimer,
        disclaimer_es: featuredOffer?.disclaimerES,
        extraDisclaimer:
          language === "es"
            ? featuredOffer?.extraDisclaimerES
            : featuredOffer?.extraDisclaimer,
        active: false,
      }
      dispatch(setDisclaimers([...disclaimers, newDisclaimer]))
      setSlideCount(slideCount + 1)
    }
  })

  offerDetails = {
    name: `${_("Featured")} ${featuredOfferType} ${_("Offer")}`,
    valueType: "usd",
    value: featuredOffer?.monthlyPayment || featuredOffer?.cashAmount,
    termType:
      featuredOffer?.cardLabel == "Lease Cash"
        ? _("LEASE CASH")
        : _("PER MONTH"),
    term: featuredOffer?.duration,
    dueAtSigning: featuredOffer?.dueAtSigningAmount,
    _id: featuredOffer?._id,
  }

  return (
    <motion.article
      animate={{ opacity: 1 }}
      initial={{ opacity: 0 }}
      transition={{ duration: 1, delay: 0.3, type: "spring" }}
      css={[
        tw`col-start-1 col-end-3 row-start-2 row-end-5 justify-self-start flex flex-col justify-start w-[fit-content] bg-black text-white px-8 py-4 mt-4 rounded-tr-xl relative`,
        tw`sm:(ml-8 rounded-t-xl)`,
        tw`xl:(col-start-1 col-end-2 row-start-2 row-end-7 justify-self-end h-auto mr-48)`,
      ]}
      className="optimizelyLeaseCard"
    >
      <motion.div
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 1.2, delay: 0.5, type: "spring" }}
        css={[
          tw`self-start items-center font-light text-white py-2`,
          tw`xl:(flex mt-3 self-start bg-none w-full)`,
        ]}
      >
        <h1
          css={[tw`text-3xl mr-5`, tw`xl:(text-5xl)`, "letter-spacing: 6px"]}
          className="optimizelyTest10DesignControl"
        >
          {_("Offers")}
        </h1>
        <h1
          css={[tw`text-3xl mr-5 hidden`, tw`xl:(text-3xl)`]}
          className="optimizelyTest10DesignVariant"
        >
          {_("Offers")}
        </h1>
        {offersLength && (
          <>
            <div css={[tw`block`]} className="optimizelyTest10DesignControl">
              <span css={[tw`font-semibold`]}>{offersLength}</span> {_("Offer")}
              {offersLength === 1 ? "" : "s"} {_("Near You")}
            </div>
            <div
              css={[tw`flex justify-end w-full gap-2 hidden`]}
              className="optimizelyTest10DesignVariant"
            >
              <span>{offersLength}</span> {_("Offer")}
              {offersLength === 1 ? "" : "s"} {_("Near You")}
            </div>
          </>
        )}
      </motion.div>
      {offerDetails?.name && (
        <>
          <motion.div
            css={[tw`text-gray-600 italic text-sm mb-2 pt-2`]}
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 1, delay: 0.4, type: "spring" }}
            className="optimizelyTest10DesignControl"
          >
            {offerDetails.name}
          </motion.div>
          <motion.div
            css={[
              tw`text-white bg-blue rounded-full text-sm mt-8  mb-3 py-1 px-3 w-fit hidden`,
            ]}
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 1, delay: 0.4, type: "spring" }}
            className="optimizelyTest10DesignVariant"
          >
            {offerDetails.name}
          </motion.div>
        </>
      )}
      {language === "en"
        ? year &&
          featuredOffer?.series?.name && (
            <>
              <motion.div
                css={[
                  tw`text-base font-light mb-2`,
                  tw`xl:(text-xl)`,
                  "letter-spacing: 2.5px",
                ]}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 1, delay: 0.45, type: "spring" }}
                className="optimizelyTest10DesignControl"
              >
                {year}
              </motion.div>
              <motion.div
                css={[
                  // shrink font size based on words length
                  featuredOffer?.series?.name.split("").length >= 7
                    ? tw`text-base md:text-xl `
                    : tw`text-xl`,
                  tw` pb-3 mb-2 font-semibold border-b-2 border-gray-700 hidden`,
                ]}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 1, delay: 0.45, type: "spring" }}
                className="optimizelyTest10DesignVariant"
              >
                <span css={[tw`mr-2`]}> {year}</span>
                {featuredOffer?.series?.name}
              </motion.div>
            </>
          )
        : featuredOffer?.series?.name && (
            <>
              <motion.div
                css={[
                  // shrink font size based on word length
                  featuredOffer?.series?.name.split("").length >= 7
                    ? tw`text-base md:text-2xl`
                    : tw`text-2xl`,
                  tw`italic font-light mb-0`,
                  "letter-spacing: 1.25rem",
                  tw`md:(italic font-light mb-0)`,
                  "letter-spacing: 1.25rem",
                  featuredOffer?.series?.name == "bZ4X"
                    ? tw`normal-case`
                    : tw`uppercase`,
                ]}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 1, delay: 0.45, type: "spring" }}
                className="optimizelyTest10DesignControl"
              >
                {featuredOffer?.series?.name}
              </motion.div>
              <motion.div
                css={[
                  // shrink font size based on words length
                  featuredOffer?.series?.name.split("").length >= 7
                    ? tw`text-base md:text-xl `
                    : tw`text-xl`,
                  tw` pb-3 mb-2 font-semibold border-b-2 border-gray-700 hidden`,
                ]}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 1, delay: 0.45, type: "spring" }}
                className="optimizelyTest10DesignVariant"
              >
                <span css={[tw`mr-2`]}>{featuredOffer?.series?.name}</span>
                {year}
              </motion.div>
            </>
          )}
      {language === "es"
        ? year && (
            <motion.div
              css={[
                tw`text-base font-light mb-4`,
                tw`xl:(text-xl)`,
                "letter-spacing: 2.5px",
              ]}
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 1, delay: 0.45, type: "spring" }}
              className="optimizelyTest10DesignControl"
            >
              {year}
            </motion.div>
          )
        : featuredOffer?.series?.name && (
            <motion.div
              css={[
                // shrink font size based on word length
                featuredOffer?.series?.name.split("").length >= 7
                  ? tw`text-base md:text-2xl`
                  : tw`text-2xl`,
                tw`italic font-light mb-4`,
                "letter-spacing: 1.25rem",
                tw`md:(italic font-light mb-4)`,
                "letter-spacing: 1.25rem",
                featuredOffer?.series?.name == "bZ4X"
                  ? tw`normal-case`
                  : tw`uppercase`,
              ]}
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 1, delay: 0.45, type: "spring" }}
              className="optimizelyTest10DesignControl"
            >
              {featuredOffer?.series?.name}
            </motion.div>
          )}
      {offerDetails && (
        <motion.div
          css={[tw`xl:mb-6`, offerDetails.termType && tw`xl:mb-10`]}
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 1, delay: 0.5, type: "spring" }}
          className="optimizelyTest10DesignControl"
        >
          <div css={[tw`flex w-full max-w-[200px]`]}>
            <div
              css={[
                tw`flex-1 flex font-light`,
                offerDetails.term &&
                  tw`items-start border-r-2 border-gray-700 pr-2`,
              ]}
            >
              <div css={[tw`text-4xl mb-2 z-30`, tw`xl:text-5xl`]}>
                {offerDetails.valueType === "usd" && (
                  <span css={[tw`text-xl align-top`, tw`xl:text-2xl`]}>$</span>
                )}
              </div>
              <div css={tw``}>
                <span css={[tw`text-4xl z-30`, tw`xl:text-5xl`]}>
                  {offerDetails.value}
                </span>
                {offerDetails.termType && (
                  <div css={[tw`text-[0.625rem] mt-[8px]`]}>
                    {offerDetails.termType}
                  </div>
                )}
              </div>
              {offerDetails.valueType === "percentage" && (
                <span css={[tw`text-xl align-top`, tw`xl:text-2xl`]}>%</span>
              )}
            </div>

            {offerDetails.term && (
              <div
                css={[
                  tw`flex-1 flex flex-col items-start font-light pl-2`,
                  offerDetails.dueAtSigning &&
                    tw`items-start xl:border-r-2 xl:border-gray-700 xl:pr-2`,
                ]}
              >
                <div css={tw`flex`}>
                  <div css={[tw`text-4xl`, tw`xl:text-5xl`]}>
                    {offerDetails.term}
                  </div>
                  <span css={[featuredOfferType !== "APR" && tw`mt-1 w-full`]}>
                    {featuredOffer?.disclaimer &&
                      parseDisclaimerBlocks(
                        `[${featuredOffer?._id}]`,
                        selection =>
                          dispatch(toggleDisclaimersModal(selection)),
                      )}
                  </span>
                </div>
                <div
                  css={[
                    tw`uppercase mt-[9px] xl:mt-[11px]`,
                    "font-size: 0.625rem",
                  ]}
                >
                  {_("MONTHS")}
                </div>
              </div>
            )}

            {/* Desktop */}
            {offerDetails?.dueAtSigning && (
              <div
                css={[
                  tw`text-4xl mb-2 z-30`,
                  tw`xl:text-5xl hidden xl:flex items-start font-light xl:ml-2`,
                ]}
              >
                <span css={[tw`text-xl align-top `, tw`xl:text-2xl`]}>$</span>
                <div>
                  <div>{toUsd(offerDetails?.dueAtSigning)}</div>
                  <div css={[tw`text-[0.625rem] uppercase mt-3.5`]}>
                    {_("due at signing")}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Mobile */}
          {offerDetails?.dueAtSigning && (
            <div
              css={[
                tw`flex max-w-[200px] w-full text-center text-xs font-light mt-5 xl:hidden`,
              ]}
            >
              <span>$</span>
              {toUsd(offerDetails?.dueAtSigning)} {_("due at signing")}
            </div>
          )}
        </motion.div>
      )}
      {offerDetails && (
        <motion.div
          css={[tw`hidden`]}
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 1, delay: 0.5, type: "spring" }}
          className="optimizelyTest10DesignVariant"
        >
          <div css={[tw`flex w-full `]}>
            <div
              css={[
                tw`flex font-light lg:flex-1`,
                offerDetails.term && tw`items-start lg:pr-2`,
              ]}
            >
              <div css={[tw`text-4xl mb-2 z-30`, tw`xl:text-5xl`]}>
                {offerDetails.valueType === "usd" && (
                  <div
                    css={[
                      tw`text-xl flex mt-2 align-top font-light`,
                      tw`xl:text-2xl`,
                    ]}
                  >
                    $
                  </div>
                )}
              </div>
              <div css={tw``}>
                <span css={[tw`text-5xl font-semibold z-30`, tw`xl:text-7xl`]}>
                  {offerDetails.value}
                </span>
              </div>
              {offerDetails.valueType === "percentage" && (
                <span css={[tw`text-xl align-top`, tw`xl:text-2xl`]}>%</span>
              )}
            </div>

            {offerDetails.term && (
              <div
                css={[
                  tw`flex-1 flex flex-col items-start font-light pl-2 text-gray-300`,
                  offerDetails.dueAtSigning && tw`items-start xl:pr-2`,
                ]}
              >
                {offerDetails.termType && (
                  <div css={[tw`lg:mt-[8px]`]}>
                    {offerDetails.termType.toLowerCase()}
                  </div>
                )}
                <div css={tw`flex`}>
                  <div css={[tw``]}>
                    {_("for")} {offerDetails.term} {_("mos")}
                  </div>
                  <span css={[tw`mt-1`]}>
                    {featuredOffer?.disclaimer &&
                      parseDisclaimerBlocks(
                        `[${featuredOffer?._id}]`,
                        selection =>
                          dispatch(toggleDisclaimersModal(selection)),
                      )}
                  </span>
                </div>

                {/* Desktop */}
                {offerDetails?.dueAtSigning && (
                  <div css={[tw`text-xs mb-2 z-30`]}>
                    ${toUsd(offerDetails?.dueAtSigning)} {_("due at signing")}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
      <motion.div
        css={[tw`hidden`, tw`xl:(flex flex-wrap justify-center gap-3 z-20)`]}
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 1, delay: 0.5, type: "spring" }}
      >
        <LocalInventoryLinks
          city={city}
          dealerCount={dealers?.length}
          inventoryCount={inventoryCount}
          series={featuredOffer?.series?.name}
          year={featuredOffer?.series?.currentYear}
          inventoryLink={inventoryLink}
          light
          analyticsId={`offers:${slideIndex + 1}`}
          extraTealiumValues={extraTealiumValues}
          fromOffers={true}
          ctaTextPrimary={ctaTextPrimary}
          ctaTextSecondary={ctaTextSecondary}
        />
      </motion.div>
      <motion.div
        css={[
          tw`hidden lg:(flex flex-wrap mt-4 text-sm text-gray-300 gap-3 z-20 uppercase hidden hover:(underline)) `,
        ]}
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 1, delay: 0.5, type: "spring" }}
        className="optimizelyTest10DesignVariant optimizelyTest10PrimaryMetric optimizelyResponsiveDesignVariant"
      >
        <Link
          to={
            language === "es"
              ? `/es/offers/${offerDetails?.slug}`
              : `/offers/${offerDetails?.slug}`
          }
          target="_self" // Needed for expired offer redirect
          forceExternal={true}
          onClick={() => {
            // Conditionally add values if they are available
            const tealOptionalValues: {
              dealer_name?: string
              dealer_code?: string
              vehicle_model_truncated?: string
              vehicle_model?: string
              vehicle_year?: string | number
            } = {
              ...vehicleData,
            }

            trackTealEvent({
              ...tealOptionalValues,
              offer_type: `${tealPageData.page_type} | ${tealPageData.page_name} | Lease | ${offerDetails?.name}`,
              alloy_offer_id: offerDetails?._id, //  Offer ID not on SDR but used for isFavorite check in useTealiumEvent - Jira ticket: SETMKT-456
              tealium_event: "view_offer",
            })
          }}
          analytics-id={
            slideIndex
              ? `offer hero:offers:${slideIndex + 1}`
              : "offer hero:offers:"
          }
        >
          {_("View Offer Details")}
        </Link>
      </motion.div>
    </motion.article>
  )
}

export default LeaseFeatureOfferCardVariant
